import React, { useState } from "react";
import client, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { useFetch, PageHeader, Loading, StatusBadge, fmtMAD, ConfirmDialog, downloadFile } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, CalendarPlus, Trash2, Pencil, Wallet, TrendingDown, Scale, Clock, FileDown } from "lucide-react";
import { BarChart, Bar, PieChart, Pie, Cell, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from "recharts";

const COLORS = ["#10B981", "#3B82F6", "#F59E0B", "#EF4444", "#8B5CF6", "#14B8A6"];
const thisMonth = () => new Date().toISOString().slice(0, 7);

function Overview() {
  const { data: s, loading } = useFetch("/finance/summary", []);
  if (loading || !s) return <Loading />;
  const cards = [
    { label: "Total Revenue", value: fmtMAD(s.revenue), icon: Wallet, cls: "text-emerald-600" },
    { label: "Total Expenses", value: fmtMAD(s.expenses), icon: TrendingDown, cls: "text-amber-600" },
    { label: "Net Balance", value: fmtMAD(s.net), icon: Scale, cls: s.net >= 0 ? "text-emerald-600" : "text-rose-600" },
    { label: "Outstanding", value: fmtMAD(s.outstanding), icon: Clock, cls: "text-rose-600" },
  ];
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {cards.map((c) => (
          <Card key={c.label} className="p-5"><div className="flex items-center justify-between"><div><p className="text-xs uppercase font-bold text-muted-foreground">{c.label}</p><p className={`text-xl font-extrabold mt-2 ${c.cls}`}>{c.value}</p></div><c.icon className={c.cls} size={22} /></div></Card>
        ))}
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="p-6"><h3 className="font-bold font-heading mb-4">Revenue vs Expenses (6 months)</h3>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={s.monthly}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
              <XAxis dataKey="month" tick={{ fontSize: 11 }} /><YAxis tick={{ fontSize: 11 }} />
              <Tooltip contentStyle={{ background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))", borderRadius: 8 }} /><Legend />
              <Bar dataKey="revenue" name="Revenue" fill="#10B981" radius={[4, 4, 0, 0]} /><Bar dataKey="expenses" name="Expenses" fill="#F59E0B" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Card>
        <Card className="p-6"><h3 className="font-bold font-heading mb-4">Expenses by Category</h3>
          {s.expenses_by_category?.length ? (
            <ResponsiveContainer width="100%" height={280}>
              <PieChart><Pie data={s.expenses_by_category} dataKey="value" nameKey="name" outerRadius={100} label={(e) => e.name}>{s.expenses_by_category.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}</Pie><Tooltip /></PieChart>
            </ResponsiveContainer>
          ) : <div className="h-[280px] flex items-center justify-center text-muted-foreground">No expenses yet</div>}
        </Card>
      </div>
    </div>
  );
}

function Payments() {
  const [month, setMonth] = useState(thisMonth());
  const [status, setStatus] = useState("");
  const q = `/finance/payments?month=${month}${status ? `&status=${status}` : ""}`;
  const { data, loading, refetch } = useFetch(q, [q]);
  const { data: players } = useFetch("/players?sort_by=name_asc", []);
  const [open, setOpen] = useState(false);
  const [gen, setGen] = useState(false);
  const [f, setF] = useState({});
  const [del, setDel] = useState(null);

  const save = async () => {
    try { if (f.id) await client.put(`/finance/payments/${f.id}`, f); else await client.post("/finance/payments", f); toast.success("Saved"); setOpen(false); setF({}); refetch(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const generate = async () => {
    try { const r = await client.post("/finance/payments/generate-monthly", { month }); toast.success(`Generated ${r.data.created} obligations`); setGen(false); refetch(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const doDelete = async () => { try { await client.delete(`/finance/payments/${del.id}`); toast.success("Archived"); setDel(null); refetch(); } catch (e) { toast.error(apiError(e)); } };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="w-44" data-testid="pay-month" />
        <Select value={status || "all"} onValueChange={(v) => setStatus(v === "all" ? "" : v)}>
          <SelectTrigger className="w-40" data-testid="pay-status-filter"><SelectValue placeholder="All statuses" /></SelectTrigger>
          <SelectContent><SelectItem value="all">All statuses</SelectItem><SelectItem value="paid">Paid</SelectItem><SelectItem value="unpaid">Unpaid</SelectItem><SelectItem value="partial">Partial</SelectItem><SelectItem value="overdue">Overdue</SelectItem></SelectContent>
        </Select>
        <div className="ms-auto flex gap-2">
          <Button variant="outline" onClick={() => downloadFile(`/export/financial-report.pdf?month=${month || ""}`, "financial_report.pdf")} data-testid="finance-pdf-report"><FileDown size={16} className="me-1" /> PDF Report</Button>
          <Button variant="outline" onClick={() => setGen(true)} data-testid="generate-monthly"><CalendarPlus size={16} className="me-1" /> Generate Monthly</Button>
          <Button className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => { setF({ month, type: "monthly", status: "paid", method: "cash" }); setOpen(true); }} data-testid="payment-add"><Plus size={16} className="me-1" /> Add Payment</Button>
        </div>
      </div>
      {loading ? <Loading /> : (
        <Card className="overflow-hidden">
          <div className="overflow-x-auto scrollbar-thin">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 border-b border-border"><tr>
                {["Receipt", "Player", "Amount", "Type", "Method", "Status", "Date", ""].map((h) => <th key={h} className="text-start px-4 py-3 text-xs uppercase tracking-wider text-muted-foreground">{h}</th>)}
              </tr></thead>
              <tbody className="divide-y divide-border">
                {(data || []).length === 0 && <tr><td colSpan={8} className="text-center py-12 text-muted-foreground">No payments for this month</td></tr>}
                {(data || []).map((p) => (
                  <tr key={p.id} className="hover:bg-muted/40" data-testid={`payment-${p.id}`}>
                    <td className="px-4 py-3 font-mono text-xs">{p.receipt_number}</td>
                    <td className="px-4 py-3 font-medium">{p.player_name}</td>
                    <td className="px-4 py-3 font-semibold">{fmtMAD(p.amount)}</td>
                    <td className="px-4 py-3 capitalize">{p.type}</td>
                    <td className="px-4 py-3 capitalize">{p.method || "—"}</td>
                    <td className="px-4 py-3"><StatusBadge status={p.status} /></td>
                    <td className="px-4 py-3 text-muted-foreground">{(p.date || "").slice(0, 10)}</td>
                    <td className="px-4 py-3 text-end whitespace-nowrap">
                      <Button size="icon" variant="ghost" title="Download receipt" onClick={() => downloadFile(`/export/receipt/${p.id}.pdf`, `receipt_${p.receipt_number || p.id}.pdf`)} data-testid={`payment-receipt-${p.id}`}><FileDown size={15} /></Button>
                      <Button size="icon" variant="ghost" onClick={() => { setF({ ...p }); setOpen(true); }} data-testid={`payment-edit-${p.id}`}><Pencil size={15} /></Button>
                      <Button size="icon" variant="ghost" className="text-rose-600" onClick={() => setDel(p)} data-testid={`payment-delete-${p.id}`}><Trash2 size={15} /></Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{f.id ? "Edit" : "Add"} Payment</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-4 py-2">
            <div className="col-span-2"><Label className="mb-1.5 block">Player</Label>
              <Select value={f.player_id || ""} onValueChange={(v) => setF({ ...f, player_id: v })}>
                <SelectTrigger data-testid="pay-player"><SelectValue placeholder="Select player" /></SelectTrigger>
                <SelectContent>{(players || []).map((p) => <SelectItem key={p.id} value={p.id}>{p.full_name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label className="mb-1.5 block">Amount (MAD)</Label><Input type="number" value={f.amount || ""} onChange={(e) => setF({ ...f, amount: e.target.value })} data-testid="pay-amount" /></div>
            <div><Label className="mb-1.5 block">Month</Label><Input type="month" value={f.month || ""} onChange={(e) => setF({ ...f, month: e.target.value })} /></div>
            <div><Label className="mb-1.5 block">Type</Label>
              <Select value={f.type} onValueChange={(v) => setF({ ...f, type: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="monthly">Monthly</SelectItem><SelectItem value="registration">Registration</SelectItem><SelectItem value="tournament">Tournament</SelectItem><SelectItem value="clothing">Clothing</SelectItem><SelectItem value="other">Other</SelectItem></SelectContent>
              </Select>
            </div>
            <div><Label className="mb-1.5 block">Status</Label>
              <Select value={f.status} onValueChange={(v) => setF({ ...f, status: v })}>
                <SelectTrigger data-testid="pay-status"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="paid">Paid</SelectItem><SelectItem value="unpaid">Unpaid</SelectItem><SelectItem value="partial">Partial</SelectItem><SelectItem value="overdue">Overdue</SelectItem></SelectContent>
              </Select>
            </div>
            <div><Label className="mb-1.5 block">Method</Label>
              <Select value={f.method} onValueChange={(v) => setF({ ...f, method: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="cash">Cash</SelectItem><SelectItem value="bank">Bank Transfer</SelectItem><SelectItem value="card">Card</SelectItem><SelectItem value="cheque">Cheque</SelectItem></SelectContent>
              </Select>
            </div>
            <div><Label className="mb-1.5 block">Date</Label><Input type="date" value={(f.date || "").slice(0, 10)} onChange={(e) => setF({ ...f, date: e.target.value })} /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={save} data-testid="pay-save">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
      <ConfirmDialog open={gen} onOpenChange={setGen} danger={false} confirmText="Generate" title={`Generate monthly fees for ${month}?`} description="Creates unpaid monthly obligations for all active players who don't already have one this month." onConfirm={generate} />
      <ConfirmDialog open={!!del} onOpenChange={(v) => !v && setDel(null)} title="Archive payment?" description="Move this payment to Trash." confirmText="Archive" onConfirm={doDelete} />
    </div>
  );
}

function Expenses() {
  const { data, loading, refetch } = useFetch("/finance/expenses", []);
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({});
  const [del, setDel] = useState(null);
  const save = async () => { try { if (f.id) await client.put(`/finance/expenses/${f.id}`, f); else await client.post("/finance/expenses", f); toast.success("Saved"); setOpen(false); setF({}); refetch(); } catch (e) { toast.error(apiError(e)); } };
  const doDelete = async () => { try { await client.delete(`/finance/expenses/${del.id}`); toast.success("Archived"); setDel(null); refetch(); } catch (e) { toast.error(apiError(e)); } };
  const CATS = ["Stadium Rental", "Coach Salaries", "Equipment", "Transportation", "Tournament", "Insurance", "Marketing", "Administrative", "Other"];
  return (
    <div className="space-y-4">
      <div className="flex justify-end"><Button className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => { setF({ category: "Other", method: "cash" }); setOpen(true); }} data-testid="expense-add"><Plus size={16} className="me-1" /> Add Expense</Button></div>
      {loading ? <Loading /> : (
        <Card className="overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 border-b border-border"><tr>{["Date", "Category", "Description", "Amount", ""].map((h) => <th key={h} className="text-start px-4 py-3 text-xs uppercase tracking-wider text-muted-foreground">{h}</th>)}</tr></thead>
            <tbody className="divide-y divide-border">
              {(data || []).length === 0 && <tr><td colSpan={5} className="text-center py-12 text-muted-foreground">No expenses yet</td></tr>}
              {(data || []).map((e) => (
                <tr key={e.id} className="hover:bg-muted/40" data-testid={`expense-${e.id}`}>
                  <td className="px-4 py-3">{e.date}</td><td className="px-4 py-3"><span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-secondary">{e.category}</span></td>
                  <td className="px-4 py-3">{e.description}</td><td className="px-4 py-3 font-semibold text-amber-600">{fmtMAD(e.amount)}</td>
                  <td className="px-4 py-3 text-end"><Button size="icon" variant="ghost" onClick={() => { setF({ ...e }); setOpen(true); }}><Pencil size={15} /></Button><Button size="icon" variant="ghost" className="text-rose-600" onClick={() => setDel(e)}><Trash2 size={15} /></Button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{f.id ? "Edit" : "Add"} Expense</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-4 py-2">
            <div><Label className="mb-1.5 block">Category</Label>
              <Select value={f.category} onValueChange={(v) => setF({ ...f, category: v })}>
                <SelectTrigger data-testid="exp-category"><SelectValue /></SelectTrigger>
                <SelectContent>{CATS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label className="mb-1.5 block">Amount (MAD)</Label><Input type="number" value={f.amount || ""} onChange={(e) => setF({ ...f, amount: e.target.value })} data-testid="exp-amount" /></div>
            <div className="col-span-2"><Label className="mb-1.5 block">Description</Label><Input value={f.description || ""} onChange={(e) => setF({ ...f, description: e.target.value })} data-testid="exp-desc" /></div>
            <div><Label className="mb-1.5 block">Date</Label><Input type="date" value={f.date || ""} onChange={(e) => setF({ ...f, date: e.target.value })} /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={save} data-testid="exp-save">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
      <ConfirmDialog open={!!del} onOpenChange={(v) => !v && setDel(null)} title="Archive expense?" description="Move this expense to Trash." confirmText="Archive" onConfirm={doDelete} />
    </div>
  );
}

export default function Finance() {
  return (
    <div>
      <PageHeader title="Finance" subtitle="Revenue, monthly payments and expenses" testid="finance-page" />
      <Tabs defaultValue="overview">
        <TabsList>
          <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
          <TabsTrigger value="payments" data-testid="tab-payments">Payments</TabsTrigger>
          <TabsTrigger value="expenses" data-testid="tab-expenses">Expenses</TabsTrigger>
        </TabsList>
        <TabsContent value="overview" className="mt-4"><Overview /></TabsContent>
        <TabsContent value="payments" className="mt-4"><Payments /></TabsContent>
        <TabsContent value="expenses" className="mt-4"><Expenses /></TabsContent>
      </Tabs>
    </div>
  );
}
